import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppContext } from '@/contexts/AppContext';

export interface SearchResult {
  noteId: string;
  title: string;
  preacher: string;
  biblical_text: string;
  cover_image_url: string | null;
  matchedIn: ('title' | 'preacher' | 'theme' | 'verse' | 'annotation')[];
  snippet?: string;
}

const norm = (s: string) => s
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

export function useGlobalSearch(query: string) {
  const { user, isAdmin } = useAppContext();
  const [allNotes, setAllNotes] = useState<any[]>([]);
  const [annotations, setAnnotations] = useState<{ note_id: string; text: string; annotation: string | null }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
       let notesQuery = supabase
         .from('smart_notes')
         .select('id, title, preacher, biblical_text, central_theme, cited_verses, cover_image_url, created_by, published, content_type');
       
       // Filter out books from global search as they are only shown in the Livros tab
       notesQuery = notesQuery.neq('content_type', 'book');
 
       if (!isAdmin) notesQuery = notesQuery.eq('created_by', user.id);

      const [{ data: notes }, { data: annots }] = await Promise.all([
        notesQuery,
        supabase.from('highlights').select('note_id, text, annotation').eq('user_id', user.id),
      ]);
      if (cancelled) return;
      setAllNotes(notes ?? []);
      setAnnotations((annots ?? []) as any);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, isAdmin]);

  const results = useMemo<SearchResult[]>(() => {
    const q = norm(query.trim());
    if (q.length < 2) return [];

    const map = new Map<string, SearchResult>();
    const noteById = new Map(allNotes.map(n => [n.id, n]));

    for (const n of allNotes) {
      const matched: SearchResult['matchedIn'] = [];
      let snippet: string | undefined;
      if (norm(n.title || '').includes(q)) matched.push('title');
      if (norm(n.preacher || '').includes(q)) matched.push('preacher');
      if (norm(n.central_theme || '').includes(q)) { matched.push('theme'); snippet = n.central_theme; }
      const verses: string[] = n.cited_verses ?? [];
      if (verses.some(v => norm(v).includes(q))) matched.push('verse');

      if (matched.length) {
        map.set(n.id, {
          noteId: n.id,
          title: n.title,
          preacher: n.preacher,
          biblical_text: n.biblical_text,
          cover_image_url: n.cover_image_url,
          matchedIn: matched,
          snippet,
        });
      }
    }

    for (const a of annotations) {
      const inText = norm(a.text || '').includes(q);
      const inAnnot = a.annotation ? norm(a.annotation).includes(q) : false;
      if (!inText && !inAnnot) continue;
      const note = noteById.get(a.note_id);
      if (!note) continue;
      const existing = map.get(a.note_id);
      const snippet = (a.annotation && inAnnot) ? a.annotation : a.text;
      if (existing) {
        if (!existing.matchedIn.includes('annotation')) existing.matchedIn.push('annotation');
        if (!existing.snippet) existing.snippet = snippet;
      } else {
        map.set(a.note_id, {
          noteId: note.id,
          title: note.title,
          preacher: note.preacher,
          biblical_text: note.biblical_text,
          cover_image_url: note.cover_image_url,
          matchedIn: ['annotation'],
          snippet,
        });
      }
    }

    return Array.from(map.values()).slice(0, 30);
  }, [query, allNotes, annotations]);

  return { results, loading };
}
